<template>
  <drag-and-drop enabled @drop-files="loadFiles" id="app-container">
    <template v-slot="{ dragHover }">
      <v-app>
        <app-bar @click:left-menu="leftSideBar = !leftSideBar"></app-bar>
        <v-navigation-drawer
          v-model="leftSideBar"
          app
          clipped
          touchless
          width="450"
          id="left-nav"
        >
          <module-panel @close="leftSideBar = false" />
        </v-navigation-drawer>
        <v-main id="content-main">
          <div
            v-if="dicomWeb.isLoading"
            class="dicom-loading-indicator"
            role="status"
            aria-live="polite"
          >
            <div class="d-flex align-center justify-space-between px-4 py-2">
              <span>Cargando estudio DICOM...</span>
              <v-progress-circular indeterminate size="16" width="2" />
            </div>
            <v-progress-linear indeterminate color="light-blue-accent-2" />
          </div>
          <div
            v-if="dicomWeb.isLoading && dicomWeb.activePreviewImage"
            class="dicom-preview"
            role="status"
            aria-live="polite"
          >
            <img
              class="dicom-preview__image"
              :src="dicomWeb.activePreviewImage"
              alt="Previsualización del corte central del estudio"
            />
            <div class="dicom-preview__status">
              <strong>Vista preliminar del corte central</strong>
              <span
                v-if="dicomWeb.activeVolumeProgress?.phase === 'Processing'"
              >
                Descarga completa. Preparando el volumen MPR y 3D...
              </span>
              <span v-else>
                El volumen completo continúa descargándose en segundo plano<span
                  v-if="previewDownloadPercent !== null"
                >
                  · {{ previewDownloadPercent }}%</span
                >.
              </span>
              <small>
                Imagen preliminar; espere la carga completa para diagnosticar.
              </small>
            </div>
          </div>
          <div class="fill-height d-flex flex-row flex-grow-1">
            <controls-strip :has-data="hasData"></controls-strip>
            <div class="d-flex flex-column flex-grow-1">
              <VtkRenderWindowParent>
                <layout-grid v-show="hasData" :layout="layout" />
              </VtkRenderWindowParent>
              <dicom-quick-stack-viewer
                v-if="quickDicomVolumeKey && !hasData"
                :volume-key="quickDicomVolumeKey"
              />
              <welcome-page
                v-if="!hasData && !quickDicomVolumeKey"
                :loading="showLoading"
                class="clickable"
                @click="loadUserPromptedFiles"
              >
              </welcome-page>
            </div>
          </div>
        </v-main>
        <controls-modal />
      </v-app>
      <persistent-overlay
        :disabled="!dragHover"
        color="#000"
        :opacity="0.3"
        :z-index="2000"
        class="text-center"
      >
        <div class="d-flex flex-column align-center justify-center h-100">
          <div class="dnd-prompt">
            <v-icon size="4.75rem">mdi-download</v-icon>
            <div class="text-h2 font-weight-bold">Drop your files to open</div>
          </div>
        </div>
      </persistent-overlay>
    </template>
  </drag-and-drop>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useDisplay } from 'vuetify';
import useLoadDataStore from '@/src/store/load-data';
import { useViewStore } from '@/src/store/views';
// Side-effect import: registers processing's config section and
// launch-load subscriber before any config or data loads.
import '@/src/processing';
import { signalLaunchLoadComplete } from '@/src/core/launchLoad';
import useRemoteSaveStateStore from '@/src/store/remote-save-state';
import AppBar from '@/src/components/AppBar.vue';
import ControlsStrip from '@/src/components/ControlsStrip.vue';
import {
  loadFiles,
  loadUserPromptedFiles,
  loadUrls,
} from '@/src/actions/loadUserFiles';
import WelcomePage from '@/src/components/WelcomePage.vue';
import { useDICOMStore } from '@/src/store/datasets-dicom';
import { useDicomWebStore } from '@/src/store/dicom-web/dicom-web-store';
import LayoutGrid from '@/src/components/LayoutGrid.vue';
import ModulePanel from '@/src/components/ModulePanel.vue';
import DragAndDrop from '@/src/components/DragAndDrop.vue';
import PersistentOverlay from '@/src/components/PersistentOverlay.vue';
import ControlsModal from '@/src/components/ControlsModal.vue';
import { useImageStore } from '@/src/store/datasets-images';
import { useServerStore } from '@/src/store/server';
import { useGlobalErrorHook } from '@/src/composables/useGlobalErrorHook';
import { useKeyboardShortcuts } from '@/src/composables/useKeyboardShortcuts';
import { useCurrentImage } from '@/src/composables/useCurrentImage';
import {
  populateAuthorizationToken,
  stripTokenFromUrl,
} from '@/src/utils/token';
import { defaultImageMetadata } from '@/src/core/progressiveImage';
import VtkRenderWindowParent from '@/src/components/vtk/VtkRenderWindowParent.vue';
import DicomQuickStackViewer from '@/src/components/dicom-web/DicomQuickStackViewer.vue';
import { useSyncWindowing } from '@/src/composables/useSyncWindowing';
import { readLaunchParams } from '@/src/utils/urlParams';

export default defineComponent({
  name: 'App',

  components: {
    ControlsStrip,
    LayoutGrid,
    DragAndDrop,
    ModulePanel,
    PersistentOverlay,
    ControlsModal,
    WelcomePage,
    AppBar,
    VtkRenderWindowParent,
    DicomQuickStackViewer,
  },

  setup() {
    const imageStore = useImageStore();
    const dicomStore = useDICOMStore();
    const dicomWeb = useDicomWebStore();

    useGlobalErrorHook();
    useKeyboardShortcuts();

    // --- sync handling --- //

    useSyncWindowing();

    // --- file handling --- //

    const loadDataStore = useLoadDataStore();
    const hasData = computed(
      () =>
        imageStore.idList.length > 0 ||
        Object.keys(dicomStore.volumeInfo).length > 0
    );
    // show loading if actually loading or has any data,
    // since the welcome screen shouldn't be visible when
    // a dataset is opened.
    const showLoading = computed(
      () => loadDataStore.isLoading || hasData.value
    );

    const { currentImageMetadata, isImageLoading } = useCurrentImage();
    const defaultImageMetadataName = defaultImageMetadata().name;
    watch(currentImageMetadata, (newMetadata) => {
      let prefix = '';
      if (
        newMetadata?.name &&
        // wait until we get a real name, but if we never do, show default name
        (newMetadata.name !== defaultImageMetadataName || !isImageLoading)
      ) {
        prefix = `${newMetadata.name} -`;
      }
      document.title = `${prefix}VolView`;
    });

    // --- parse URL -- //

    // A `tokenUrl=` bearer is fetched asynchronously; await it before loading so
    // the first data requests carry the Authorization header.
    const authReady = populateAuthorizationToken();
    stripTokenFromUrl();

    const urlParams = readLaunchParams();

    onMounted(async () => {
      await authReady;
      await loadUrls(urlParams);
      // Feature entry points subscribe to this (see launchLoad.ts).
      await signalLaunchLoadComplete();
    });

    // --- remote save state URL --- //

    if (urlParams.save) {
      const url = Array.isArray(urlParams.save)
        ? urlParams.save[0]
        : urlParams.save;
      useRemoteSaveStateStore().setSaveUrl(url);
    }

    // --- remote server --- //

    const serverStore = useServerStore();
    onMounted(() => {
      serverStore.connect();
    });

    // --- layout --- //

    const { visibleLayout } = storeToRefs(useViewStore());

    // --- //

    const display = useDisplay();
    const opensDicomWebStudy = new URLSearchParams(window.location.search).has(
      'dicomweb'
    );
    const leftSideBar = ref(
      dicomWeb.quick2DMode ? false : !display.mobile.value || opensDicomWebStudy
    );

    const quickDicomVolumeKey = computed(() => {
      if (
        !dicomWeb.quick2DMode ||
        dicomWeb.fetchDicomsProgress !== 'Done' ||
        !dicomWeb.activeVolumeKey
      )
        return null;

      return dicomWeb.volumes[dicomWeb.activeVolumeKey]?.state === 'Done'
        ? null
        : dicomWeb.activeVolumeKey;
    });

    const notifyEmbeddedViewer = (
      status: 'loading' | 'preview' | 'ready' | 'error'
    ) => {
      if (window.parent !== window) {
        window.parent.postMessage({ source: 'volview', status }, '*');
      }
    };

    if (opensDicomWebStudy) {
      onMounted(() => {
        leftSideBar.value = !dicomWeb.quick2DMode;
        notifyEmbeddedViewer('loading');
      });

      watch(
        () => dicomWeb.fetchDicomsProgress,
        (progress) => {
          if (progress === 'Pending') notifyEmbeddedViewer('loading');
          if (progress === 'Done' && !dicomWeb.isLoading)
            notifyEmbeddedViewer('ready');
        }
      );

      watch(
        () => dicomWeb.isLoading,
        (isLoading) => {
          if (isLoading) notifyEmbeddedViewer('loading');
          else if (dicomWeb.fetchDicomsProgress === 'Done')
            notifyEmbeddedViewer('ready');
        }
      );

      watch(
        () => dicomWeb.activePreviewImage,
        (previewImage) => {
          if (previewImage) notifyEmbeddedViewer('preview');
        },
        { immediate: true }
      );
    }

    const previewDownloadPercent = computed(() => {
      const progress = dicomWeb.activeVolumeProgress;
      if (!progress?.total) return null;
      return Math.min(
        100,
        Math.round((progress.loaded / progress.total) * 100)
      );
    });

    return {
      leftSideBar,
      loadUserPromptedFiles,
      loadFiles,
      hasData,
      showLoading,
      dicomWeb,
      previewDownloadPercent,
      quickDicomVolumeKey,
      layout: visibleLayout,
    };
  },
});
</script>

<style>
#content-main {
  /* disable v-content transition when we resize our app drawer */
  transition: initial;
  width: 100%;
  height: 100%;
  position: fixed;
}

#left-nav {
  border-right: 1px solid rgb(var(--v-theme-background));
}

#content-main > .v-content__wrap {
  display: flex;
}

#module-switcher .v-input__prepend-inner {
  /* better icon alignment */
  margin-top: 15px;
}

.alert > .v-snack__wrapper {
  /* transition background color */
  transition: background-color 0.25s;
}
</style>

<style src="@/src/components/styles/utils.css"></style>

<style scoped>
#app-container {
  width: 100%;
  height: 100%;
}

.dnd-prompt {
  background: rgba(0, 0, 0, 0.4);
  color: white;
  border-radius: 8px;
  box-shadow: 0px 0px 10px 5px rgba(0, 0, 0, 0.4);
  padding: 64px;
}

.dicom-loading-indicator {
  position: fixed;
  top: 48px;
  left: 0;
  right: 0;
  z-index: 1005;
  background: rgba(30, 30, 30, 0.96);
  color: white;
  font-size: 0.875rem;
}

.dicom-preview {
  position: absolute;
  inset: 48px 0 0;
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  padding: 24px;
  background: #101820;
  color: white;
}

.dicom-preview__image {
  display: block;
  max-width: min(70vw, 720px);
  max-height: calc(100vh - 210px);
  object-fit: contain;
  border: 1px solid rgb(255 255 255 / 16%);
  border-radius: 4px;
  box-shadow: 0 18px 55px rgb(0 0 0 / 38%);
}

.dicom-preview__status {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  text-align: center;
}

.dicom-preview__status span,
.dicom-preview__status small {
  color: rgb(255 255 255 / 72%);
}
</style>
